/**
 * 戰力分數(Combat Power Score)— R-004 包 A「成長可視化」。
 *
 * boss 回饋:「沒有越打越強的感覺」→ 把學習驅動的戰力成長變成一個看得見、
 * 會隨學習單調上升的數字。
 *
 * ## 鐵則(學習/遊戲分離,CLAUDE.md 最高優先)
 * 戰力 **只**來自學習軌資料:
 *   - `skillLevels`(技能等級,由 factionXP 解鎖/強化 — 見 shared/skill-level.ts)。
 * 本模組是**純函式、唯讀**:不寫入任何 progress 欄位、不呼叫 addFactionXP /
 * addFreeXP / addCommanderXp。因此即使被遊戲側(HubScene)import 也不違反
 * `tests/game/learning-game-separation.test.ts`(該守門只禁「寫入」三個入帳函式)。
 *
 * 絕不讀取 `freeXP` / `commanderXp` / `commanderLevel` — 那是「玩遊戲軌」帳號
 * 廣度,與戰力深度無關(灌進戰力 = 鐵則紅線)。
 *
 * ## 公式(單調遞增,直觀對應「越打越強」)
 * 對每個「已解鎖」技能(skillLevel ≥ 1):
 *   貢獻 = BASE_PER_SKILL × getLevelMultiplier(level)
 * 戰力 = round(Σ 貢獻)。
 *
 * 性質:
 *   - 解鎖一個新技能 → 戰力 +BASE_PER_SKILL(Lv1 mult=1.0 → +100)。
 *   - 既有技能升 1 級 → mult 提高 → 戰力增加(Lv1→Lv5: 100→210)。
 *   - 兩種「學習成長」都讓數字變大 ⇒ 直接給「越打越強」回饋。
 */
import type { PlayerProgress } from '../../shared/types';
import { getLevelMultiplier, MAX_SKILL_LEVEL } from '../../shared/skill-level';

/** 每個已解鎖技能在 Lv1(mult 1.0)時的基礎戰力貢獻。整數方便顯示。 */
export const BASE_POWER_PER_SKILL = 100;

/**
 * 計算玩家當前戰力分數(combat power score)。
 *
 * 只讀 `progress.skillLevels`。任何 progress 缺值/舊存檔都防禦性處理:
 * skillLevels 不存在 → 視為空,戰力 0。等級會夾在 [0, MAX_SKILL_LEVEL]。
 *
 * @returns 非負整數戰力分數。沒有任何解鎖技能 → 0。
 */
export function computeCombatPower(progress: Pick<PlayerProgress, 'skillLevels'> | null | undefined): number {
  const levels = progress?.skillLevels;
  if (!levels) return 0;

  let total = 0;
  for (const rawLevel of Object.values(levels)) {
    const lv = typeof rawLevel === 'number' ? rawLevel : 0;
    if (lv < 1) continue; // 未解鎖不計
    const clamped = Math.min(lv, MAX_SKILL_LEVEL);
    total += BASE_POWER_PER_SKILL * getLevelMultiplier(clamped);
  }
  return Math.round(total);
}

/**
 * 兩份 progress 的戰力差(after − before)。供「學習入帳跳 ⚡戰力+N」用。
 * 結果可能為 0(無成長)或正數;理論上學習只會增不減,但仍如實回傳。
 */
export function combatPowerDelta(
  before: Pick<PlayerProgress, 'skillLevels'> | null | undefined,
  after: Pick<PlayerProgress, 'skillLevels'> | null | undefined,
): number {
  return computeCombatPower(after) - computeCombatPower(before);
}
